import { WINDOWS } from '../Platform.js';
import { getFromStorage, setInStorage } from '../common/StorageHelper.js';
import {
  STORAGE_PROHIBITED_ACTION_COUNTER,
  STORAGE_PAUSED_REASON,
  STORAGE_PAUSED_BY,
  STORAGE_ISFULLSCREEN,
  STORAGE_PNP,
  STORAGE_PROCTOR_RESUME_NEEDED,
} from '../../components/common/Storage.js';
import * as PwaEvents from '../../context/PwaEvents.js';
import * as TestStatus from '../../components/assessment/TestStatus.js';
import { hasNetworkErrors } from '../network-error/networkError.js';

export const VIOLATION_COUNT_PLACEHOLDER = 'VIOLATION_COUNT_PLACEHOLDER';

export const PROHIBITED_ACTION = {
  //no need to internationalize PREFIX & type because these are used in session storage.
  PREFIX: 'Prohibited Action: ',
  OUT_OF_FOCUS: {
    type: 'OUT_OF_FOCUS',
    description: 'components.lessThanSecure.outOfFocusDescription',
  },
  CONTEXT_MENU: {
    type: 'CONTEXT_MENU',
    description: 'components.lessThanSecure.contextMenuDescription',
  },
  PRINT: {
    type: 'PRINT',
    description: 'components.lessThanSecure.printDescription',
  },
  SAVE: {
    type: 'SAVE',
    description: 'components.lessThanSecure.saveDescription',
  },
  SCREEN_CAPTURE: {
    type: 'SCREEN_CAPTURE',
    description: 'components.lessThanSecure.screenCaptureDescription',
  },
  COMMAND_KEY: {
    type: 'COMMAND_KEY', //This can be metaKey(window or command), Ctrl + Shift
    description: 'components.lessThanSecure.commandKeyDescription',
  },
  NOT_FULLSCREEN: {
    type: 'NOT_FULLSCREEN',
    description: 'components.lessThanSecure.notFullscreenDescription',
    messageNote: 'components.lessThanSecure.notFullscreenMessageNote',
  },
  NOT_FULLSCREEN_PROCTOR_RESUME_ONLY: {
    type: 'NOT_FULLSCREEN_PROCTOR_RESUME_ONLY',
    description: 'components.lessThanSecure.notFullscreenProctorResumeOnlyDescription',
  },
  SPLIT_SCREEN: {
    type: 'SPLIT_SCREEN',
    description: 'components.lessThanSecure.splitScreenDescription',
  },
};

export const clearClipboard = async (event) => {
  try {
    await navigator.clipboard.writeText('');
  } catch (error) {
    console.error(error);
  }
};

const attachClearingOfClipboard = () => {
  let learnosityDiv = document.getElementById('learnosity_assess');
  if (learnosityDiv) {
    learnosityDiv.addEventListener('click', clearClipboard, { once: true });
    learnosityDiv.addEventListener('keydown', clearClipboard, {
      once: true,
    });
  }
};

//increments and returns current count
export const incrementViolationCount = () => {
  let pausedReason = getFromStorage(STORAGE_PAUSED_REASON);
  let violationCount = getFromStorage(STORAGE_PROHIBITED_ACTION_COUNTER);
  if (!violationCount) {
    violationCount = 0;
  }
  if (
    pausedReason ===
      PROHIBITED_ACTION.PREFIX + PROHIBITED_ACTION.NOT_FULLSCREEN.type ||
    isFullscreen()
  ) {
    return parseInt(violationCount);
  } else {
    ++violationCount;
    setInStorage(STORAGE_PROHIBITED_ACTION_COUNTER, violationCount);
    return violationCount;
  }
};

export const checkReadyToResume = () => {
  let proctorResumeNeeded = getFromStorage(STORAGE_PROCTOR_RESUME_NEEDED);
  let readyToResume = !waitToResume();
  return proctorResumeNeeded && readyToResume;
};

export let isScreenSplit;

export const enforceLessSecure = (
  config,
  showProhibitedActionDialog,
  platform,
  notify,
) => {
  const preventableEventHandler = async (event, action) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    showProhibitedActionDialog(action);
    return false;
  };

  const nonPreventableEventHandler = async (event, action) => {
    showProhibitedActionDialog(action);
    return false;
  };

  const copyCutEventHandler = async (event) => {
    event.clipboardData.setData('text/plain', '');
    event.preventDefault();
    event.stopImmediatePropagation();
    return false;
  };

  const sendReadyToResume = () => {
    if (checkReadyToResume()) {
      notify({
        eventName: PwaEvents.READY_TO_RESUME,
      });
    }
  };

  let eventHandlers = {
    copyCut: copyCutEventHandler,
    focus: (event) => {
      sendReadyToResume();
    },
    blur: async (event) => {
      attachClearingOfClipboard();
      //clear window selection, to prevent paste directly to text editor without prior document.click
      if (window.getSelection) {
        if (window.getSelection().empty) {
          // Chrome
          window.getSelection().empty();
        } else if (window.getSelection().removeAllRanges) {
          // Firefox
          window.getSelection().removeAllRanges();
        }
      } else if (document.selection) {
        // IE?
        document.selection.empty();
      }
      if (!config.enableOutOfFocus) {
        showProhibitedActionDialog(PROHIBITED_ACTION.OUT_OF_FOCUS);
      }
    },
    beforeprint: (event) => {
      nonPreventableEventHandler(event, PROHIBITED_ACTION.PRINT);
    },
    contextmenu: (event) => {
      preventableEventHandler(event, PROHIBITED_ACTION.CONTEXT_MENU);
    },
    keydown: async (event) => {
      //handles both mac&windows command
      //all windows command is not allowed in windows os, this will help prevent screenshot in windows
      //note: we cannot prevent metaKey in all OS since this metaKey is mostly used in copy/cut/paste in macos inside the editor section
      if (platform === WINDOWS && event.metaKey) {
        preventableEventHandler(event, PROHIBITED_ACTION.COMMAND_KEY); //we say command key because we are not sure if it's really a print screen that goes with it
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
        preventableEventHandler(event, PROHIBITED_ACTION.PRINT);
        return;
      }
      //handles both mac&windows command
      if ((event.metaKey || event.ctrlKey) && event.key === 's') {
        preventableEventHandler(event, PROHIBITED_ACTION.SAVE);
        return;
      }

      //screenshot for mac, chromeOS and windows
      //note: In ChromeOS, not all screenshot command can be capture: CTRL + show window - cannot capture 'Show Window' key at all.
      if ((event.metaKey || event.ctrlKey) && event.shiftKey) {
        preventableEventHandler(event, PROHIBITED_ACTION.COMMAND_KEY); //we say command key because we are not sure if it's really a print screen that goes with it
        return;
      }
    },
    drag: (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    },
    keyup: (event) => {
      //Support for capturing Print Screen in Windows
      //only captures the command - Print Screen key alone.
      //Windows+Print screen is captured in keydown(metakey)
      if (event.key === 'PrintScreen' && !event.metaKey) {
        preventableEventHandler(event, PROHIBITED_ACTION.SCREEN_CAPTURE); // we are sure here that it's a print screen
      }
    },
    mediaWatcher: window.matchMedia('(display-mode: fullscreen)'), //need to store exact mediaMatcher to be able to remove listener
    windowMatchMediaChange: ({ matches }) => {
      //this will handle exiting fullscreen in non-safari
      if (!matches) {
        setFullscreen(false);
        nonPreventableEventHandler(null, PROHIBITED_ACTION.NOT_FULLSCREEN);
      }
    },
    fullscreenApiChange: () => {
      setFullscreen(isApiFullscreen());
      if (!isApiFullscreen()) {
        //this will handle exiting fullscreen in safari - safari is consistent, exiting with any method will trigger fullscreenchange/webkitfullscreenchange
        if (isWebKit()) {
          nonPreventableEventHandler(null, PROHIBITED_ACTION.NOT_FULLSCREEN);
        }
      } else {
        sendReadyToResume();
      }
    },
    splitscreen: () => {
      if (!isFullscreen()) {
        return;
      }
      const isOpen = checkIfScreenIsSplit();
      if (isScreenSplit === isOpen) {
        return;
      }
      isScreenSplit = isOpen;
      if (isScreenSplit) {
        showProhibitedActionDialog(PROHIBITED_ACTION.SPLIT_SCREEN);
        return;
      } else {
        sendReadyToResume();
      }
    },
  };

  if (!config.enableKeyCommands) {
    window.addEventListener('copy', eventHandlers.copyCut);
    window.addEventListener('cut', eventHandlers.copyCut);
    window.addEventListener('beforeprint', eventHandlers.beforeprint);
    document.addEventListener('keydown', eventHandlers.keydown);
    document.addEventListener('keyup', eventHandlers.keyup);
    document.addEventListener('dragstart', eventHandlers.drag);
  }

  if (!config.allowSplitScreen) {
    setInterval(() => {
      window.dispatchEvent(new CustomEvent('splitscreen'));
    }, 1000); //Check dev tools
    window.addEventListener('splitscreen', eventHandlers.splitscreen);
    window.visualViewport?.addEventListener('splitscreen', eventHandlers.splitscreen);
  }

  window.addEventListener('blur', eventHandlers.blur);
  if (!config.enableOutOfFocus) {
    window.addEventListener('focus', eventHandlers.focus);
  }

  if (!config.enableRightClick) {
    window.addEventListener('contextmenu', eventHandlers.contextmenu);
  }

  if (!config.allowNonFullscreen) {
    if (!isWebKit()) {
      //add event handler watch for fullscreen
      eventHandlers.mediaWatcher.addEventListener(
        'change',
        eventHandlers.windowMatchMediaChange,
      );
    }
    document.addEventListener(
      'fullscreenchange',
      eventHandlers.fullscreenApiChange,
    );
    document.addEventListener(
      'webkitfullscreenchange',
      eventHandlers.fullscreenApiChange,
    );
  } else {
    //attach clipboard clearing on test start if allowNonFullscreen -- this is because there is no proceed button for this case
    attachClearingOfClipboard();
  }

  return eventHandlers;
};

export const removeEventListeners = (eventHandlers) => {
  window.removeEventListener('contextmenu', eventHandlers?.contextmenu);

  window.removeEventListener('blur', eventHandlers?.blur);

  window.removeEventListener('focus', eventHandlers.focus);

  window.removeEventListener('copy', eventHandlers?.copyCut);

  window.removeEventListener('cut', eventHandlers?.copyCut);

  window.removeEventListener('beforeprint', eventHandlers?.beforeprint);

  document.removeEventListener('drag', eventHandlers?.drag);

  document.removeEventListener('keydown', eventHandlers?.keydown);

  document.removeEventListener('keyup', eventHandlers?.keyup);

  eventHandlers?.mediaWatcher?.removeEventListener(
    'change',
    eventHandlers.windowMatchMediaChange,
  );
  document.removeEventListener(
    'fullscreenchange',
    eventHandlers.fullscreenApiChange,
  );
  document.removeEventListener(
    'webkitfullscreenchange',
    eventHandlers.fullscreenApiChange,
  );
  window.removeEventListener('splitscreen', eventHandlers.splitscreen);
  window.visualViewport?.removeEventListener(
    'splitscreen',
    eventHandlers.splitscreen,
  );
};

//checks if browser is safari
const isWebKit = () => {
  const ua = navigator.userAgent;
  // Check for "AppleWebKit" and the absence of "Chrome".
  return /AppleWebKit/.test(ua) && !/Chrome/.test(ua);
};

export const isApiFullscreen = () => {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    null
  );
};
export const isFullscreen = () => {
  return getFromStorage(STORAGE_ISFULLSCREEN);
};

export const setFullscreen = (isFullscreen) => {
  setInStorage(STORAGE_ISFULLSCREEN, isFullscreen);
};

const exitApiFullscreen = async () => {
  if (document.exitFullscreen) {
    await document.exitFullscreen();
  } else if (document.webkitExitFullscreen) {
    await document.webkitExitFullscreen();
  } else if (document.webkitCancelFullScreen) {
    await document.webkitCancelFullScreen();
  }
};

export const switchToFullscreen = async () => {
  //this part is very important. note that in non-safari browsers, if you exit fullscreen in some methods - example clicking min button
  //document.fullscreenElement will NOT be updated, and it will think it's still in fullscreen.
  //if document.fullscreenElement is NOT NULL, but browser is actually not in fullscreen, you cannot request fullscreen again
  if (isApiFullscreen()) {
    await exitApiFullscreen();
  }
  const elem = document.documentElement;
  if (elem?.requestFullscreen) {
    await elem.requestFullscreen();
  } else if (elem?.webkitRequestFullscreen) {
    await elem.webkitRequestFullscreen();
  } else if (elem?.webkitRequestFullScreen) {
    await elem.webkitRequestFullScreen();
  }
};

export const checkIfScreenIsSplit = () => {
  const height = window.visualViewport?.height || window.innerHeight;
  const width = window.visualViewport?.width || window.innerWidth;

  //determine the outer width and height based on full screen
  const inFullScreen = isFullscreen();
  const outerHeight = inFullScreen ? window.screen.height : window.outerHeight;
  const outerWidth = inFullScreen ? window.screen.width : window.outerWidth;

  //find the difference
  const widthGap = outerWidth - width;
  const heightGap = outerHeight - height;

  //calculate
  const thresholdPercent = isWebKit() ? 0.40 : 0.17;
  const widthThreshold = Math.max(outerWidth * thresholdPercent, 170);
  const heightThreshold = Math.max(outerHeight * thresholdPercent, 170);

  const isOpen = widthGap > widthThreshold || heightGap > heightThreshold;
  return isOpen;
};

//Config format:
// {
//   enableKeyCommands : true|false, this controls - Cut/Copy/Paste/Print/Save/Print Screen
//   enableRightClick : true|false,  this controls context menu/right click
//   enableOutOfFocus : true|false, this controls dialog is shown if focus is lost
// }
export const getConfigBasedOnPnp = () => {
  let pnp = getFromStorage(STORAGE_PNP);
  if (pnp?.screenReader) {
    return {
      enableOutOfFocus: true,
      //DEV-ONLY: enable out of focus comment the attribute "allowNonFullscreen" below
      allowNonFullscreen: true,
      allowSplitScreen: true
    };
  }
};

export const waitToResume = () => {
  let config = getConfigBasedOnPnp() || {};
  return (
    //if not in focus
    (!config.enableOutOfFocus && !document.hasFocus()) ||
    //if not in fullscreen
    (!config.allowNonFullscreen && !isFullscreen()) ||
    //devTool is open or screen is split
    (!config.allowSplitScreen && isScreenSplit) ||
    //has learnosity or websocket connection issues
    hasNetworkErrors()
  );
};

//check if there is a previous paused reason, based on the prev, determine which is higher
export const getPriorityAction = (action) => {
  let priorityAction = action;
  if (action === PROHIBITED_ACTION.NOT_FULLSCREEN) {
    //Only the following cases will take precedence over NOTFULLSCREEEN violation
    //1. paused by system & another prohibited action
    //2. paused by proctor
    let pausedBy = getFromStorage(STORAGE_PAUSED_BY);
    if (pausedBy === TestStatus.PAUSED_BY_SYSTEM) {
      let previousPausedReason = getFromStorage(STORAGE_PAUSED_REASON);
      if (previousPausedReason?.startsWith(PROHIBITED_ACTION.PREFIX)) {
        let actionKey = previousPausedReason.replace(
          PROHIBITED_ACTION.PREFIX,
          '',
        );
        priorityAction = PROHIBITED_ACTION[actionKey];
      }
    } else if (pausedBy === TestStatus.PAUSED_BY_PROCTOR) {
      priorityAction = PROHIBITED_ACTION.NOT_FULLSCREEN_PROCTOR_RESUME_ONLY;
    }    
  }
  return priorityAction;
};
